import React from 'react';
import {
    PieChart, Pie, Cell,
    BarChart, Bar,
    LineChart, Line,
    AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { ChartOutput } from '../types';

interface ChartViewerProps {
    data: ChartOutput;
}

const COLORS = [
    '#3b82f6',
    '#8b5cf6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#06b6d4',
    '#ec4899',
    '#6366f1',
];

export const ChartViewer: React.FC<ChartViewerProps> = ({ data }) => {
    const renderChart = () => {
        const chartType = data.chartType.toLowerCase();

        if (chartType === 'pie') {
            return (
                <ResponsiveContainer width="100%" height={500}>
                    <PieChart>
                        {data.series.map((series, seriesIdx) => (
                            <Pie
                                key={seriesIdx}
                                data={series.data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={150}
                                label
                            >
                                {series.data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                        ))}
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            );
        }

        if (chartType === 'bar') {
            return (
                <ResponsiveContainer width="100%" height={500}>
                    <BarChart data={data.series[0].data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey={data.xAxis?.dataKey || 'name'}
                            label={data.xAxis?.label ? { value: data.xAxis.label, position: 'insideBottom', offset: -5 } : undefined}
                        />
                        <YAxis
                            label={data.yAxis?.label ? { value: data.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                        />
                        <Tooltip />
                        <Legend />
                        {data.series.map((series, idx) => (
                            <Bar
                                key={idx}
                                dataKey={data.yAxis?.dataKey || 'value'}
                                fill={series.color || COLORS[idx % COLORS.length]}
                                name={series.name}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (chartType === 'line') {
            return (
                <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={data.series[0].data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey={data.xAxis?.dataKey || 'name'}
                            label={data.xAxis?.label ? { value: data.xAxis.label, position: 'insideBottom', offset: -5 } : undefined}
                        />
                        <YAxis
                            label={data.yAxis?.label ? { value: data.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                        />
                        <Tooltip />
                        <Legend />
                        {data.series.map((series, idx) => (
                            <Line
                                key={idx}
                                type="monotone"
                                dataKey={data.yAxis?.dataKey || 'value'}
                                stroke={series.color || COLORS[idx % COLORS.length]}
                                name={series.name}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            );
        }

        if (chartType === 'area') {
            return (
                <ResponsiveContainer width="100%" height={500}>
                    <AreaChart data={data.series[0].data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey={data.xAxis?.dataKey || 'name'}
                            label={data.xAxis?.label ? { value: data.xAxis.label, position: 'insideBottom', offset: -5 } : undefined}
                        />
                        <YAxis
                            label={data.yAxis?.label ? { value: data.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                        />
                        <Tooltip />
                        <Legend />
                        {data.series.map((series, idx) => (
                            <Area
                                key={idx}
                                type="monotone"
                                dataKey={data.yAxis?.dataKey || 'value'}
                                stroke={series.color || COLORS[idx % COLORS.length]}
                                fill={series.color || COLORS[idx % COLORS.length]}
                                fillOpacity={0.6}
                                name={series.name}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            );
        }

        return (
            <div className="flex items-center justify-center h-96">
                <p className="text-gray-500">Ukjent chart-type: {chartType}</p>
            </div>
        );
    };

    return (
        <div className="w-full">
            {renderChart()}
        </div>
    );
};
